<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import axios from "axios";
import { useRoute } from "vue-router";

import ClientInfoCard from "@/components/modals/ClientInfoModal.vue";
import {format} from "date-fns";
import router from "@/router";

const route = useRoute();

const request_id = route.query.request_id;

const request_details_url = "request_details/";
const request_statuses_url = "request_statuses/";

const form_data = ref({
  id: "",
  username: "",
  client_id: "",
  client_name: "",
  client_firstname: "",
  client_lastname: "",
  client_middlename: "",
  client_phone: "",
  client_address: "",
  area: "",
  street: "",
  house: "",
  flat: "",
  request_status_id: "",
  request_status_name: "",
  request_type: "",
  request_kind: "",
  manager_id: "",
  manager_name: "",
  manager_comment: "",
  fitter_id: "",
  fitter_name: "",
  fitter_comment: "",
  date_accepted: "",
  date_created: "",
  date_completed: "",
  date_closed: "",
  date_expire8: "",
  date_expire48: "",
});

let request_statuses = ref([]);

const timer = ref(null); // Reference for interval timer
const remainingTime = ref(""); // Display formatted countdown timer
const isLoading = ref(true);

// Calculate the remaining time for the timer
const calculateRemainingTime = () => {
  if (!form_data.value.date_expire48) return;

  // Parse the expiry date as UTC
  const expiryDate = new Date(form_data.value.date_expire48);
  const currentTime = new Date();

  // Get the difference in milliseconds between expiry date and the current UTC time
  const diffInMilliseconds = expiryDate.getTime() - currentTime.getTime();

  if (diffInMilliseconds <= 0) {
    remainingTime.value = "00:00:00"; // Timer reached zero
    clearInterval(timer.value); // Stop the interval
  } else {
    // Calculate hours, minutes, and seconds based on the time difference
    const hours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((diffInMilliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffInMilliseconds % (1000 * 60)) / 1000);

    // Display the timer as "HH:MM:SS"
    remainingTime.value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
};

const fetchRequest = async () => {
  isLoading.value = true;
  try {
    const res = await axios.get(request_details_url, {
      params: {
        request_id: request_id,
      },
    });

    if (res.data) {
      const data = res.data;
      form_data.value = { ...form_data.value, ...data };

      // Start the countdown after the request data is loaded
      if (form_data.value.date_expire8) {
        calculateRemainingTime();
        timer.value = setInterval(calculateRemainingTime, 1000); // Update every second
      }
    } else {
      console.error("No data found for this request.");
    }
  } catch (err) {
    console.error("Error fetching request:", err);
  } finally {
    isLoading.value = false;
  }
};

const fetchRequestTypes = async () => {
  try {
    const res = await axios.get(request_statuses_url);

    if (res.data) {
      const data = res.data;
      request_statuses.value = data.map((r_type) => ({
        request_type_id: r_type.id,
        name: r_type.name,
      }));
    }
  } catch (error) {
    console.error("API response {/request_sta}: " + error.data);
  }
};

function requestTableStatusColor(status, date_expire) {
  const current_date = new Date();
  const expire_date = new Date(date_expire);

  if (
      current_date > expire_date &&
      ((status !== "12") &&
          (status !== "13") &&
          (status !== "5") &&
          (status !== "14"))
  )
  {
    // console.log(current_date > expire_date && (status !== 'Выполнен монтёром' || status !== 'Выполнено'))
    return 'table-red';
  }

  switch (status) {
    case '13':
      return ['table-green','text-black'];
    case '12':
      return ['table-blue','text-black']
    case '5' : case '14':
      return ['table-yellow', 'text-black']
    case '8':
    case '9':
      return ['table-red','text-black']
    default:
      return ['table-white','text-black']
  }
}

const formattedTimestamp = (datetime) => {
  if (!datetime) return "—"; // Better display for empty values

  const date = new Date(datetime);
  if (isNaN(date.getTime())) return "—";

  return format(date, 'dd-MM-yyyy, HH:mm');
};

// Check if we have active timing
const hasExpiration = () => {
  return form_data.value.date_created && form_data.value.date_expire48 && !form_data.value.date_completed;
};
const goBack = () => {
  router.go(-1);
};
// Clean up the interval on component unmount
onUnmounted(() => {
  if (timer.value) clearInterval(timer.value);
});

onMounted(async () => {
  await Promise.all([fetchRequest(), fetchRequestTypes()]);
  if (form_data.value.date_expire48) {
    calculateRemainingTime();
    timer.value = setInterval(calculateRemainingTime, 1000);
  }
});
</script>

<template>
  <div class="content-wrapper">
    <div class="container-xxl px-4 py-5">
      <!-- Loading Spinner -->
      <div v-if="isLoading" class="d-flex justify-content-center my-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else class="row justify-content-center">
        <div class="col-12">
          <div class="card border-0 shadow-sm rounded-4">
            <!-- Card Header with Back Button and Status -->
            <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center py-4 px-4 px-md-5">
              <button v-if="router.options.history.state.back" @click="goBack" class="btn btn-outline-secondary d-flex align-items-center">
                <i class="fas fa-arrow-left me-2"></i> Назад
              </button>
              <div class="d-flex flex-row align-items-center">
                <div class="me-2">
                  <h5 class="text-bold">Статус: </h5>
                </div>
                <span class="badge rounded-pill fs-6 px-3 py-2" :class="requestTableStatusColor(form_data.request_status_id)">
                  {{ form_data.request_status_name }}
                </span>
                <div v-if="hasExpiration()" class="mt-2 text-center">
                  <span class="d-block small text-muted">Оставшееся время:</span>
                  <span class="fw-bold" :class="{'text-danger': remainingTime === '00:00:00'}">{{ remainingTime }}</span>
                </div>
              </div>
            </div>

            <div class="card-body p-4 p-md-5">
              <!-- Main Content and Timeline Container -->
              <div class="row g-4">
                <!-- Request Details Section -->
                <div class="col-12 col-lg-8">
                  <!-- Address Information -->
                  <div class="detail-section">
                    <h5 class="section-title">
                      <i class="bi bi-geo-alt-fill me-2 text-primary"></i>
                      Информация об адресе
                    </h5>
                    <div class="card bg-light border-0 rounded-4 p-3">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">Участок:</label>
                            <p>{{ form_data.area || '—' }}</p>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">Улица:</label>
                            <p>{{ form_data.street || '—' }}</p>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">Дом:</label>
                            <p>{{ form_data.house || '—' }}</p>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">Квартира:</label>
                            <p>{{ form_data.flat || '—' }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Client Information -->
                  <div class="detail-section">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h5 class="section-title">
                        <i class="bi bi-person-fill me-2 text-primary"></i>
                        Информация о клиенте
                      </h5>
                      <button class="btn btn-sm btn-outline-primary rounded-pill" data-bs-toggle="modal" data-bs-target="#info">
                        <i class="fas fa-info-circle me-1"></i> Подробнее
                      </button>
                    </div>
                    <div class="card bg-light border-0 rounded-4 p-3">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">ИИН:</label>
                            <p>{{ form_data.username || '—' }}</p>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">ФИО:</label>
                            <p>{{ (form_data.client_lastname + ' ' + form_data.client_firstname + ' ' + form_data.client_middlename).trim() || '—' }}</p>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">Телефон:</label>
                            <p>{{ form_data.client_phone || '—' }}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Modal -->
                    <client-info-card
                        v-if="form_data.client_id"
                        :client_firstname="form_data.client_firstname"
                        :client_lastname="form_data.client_lastname"
                        :client_middlename="form_data.client_middlename"
                        :client_username="form_data.username"
                        :client_phone="form_data.client_phone"
                        :client_id="form_data.client_id"
                        :show_popup="true"
                    ></client-info-card>
                  </div>

                  <!-- Request Type and Details -->
                  <div class="detail-section">
                    <h5 class="section-title">
                      <i class="bi bi-file-earmark-text-fill me-2 text-primary"></i>
                      Тип и вид заявки
                    </h5>
                    <div class="card bg-light border-0 rounded-4 p-3">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">Тип заявки:</label>
                            <p>{{ form_data.request_type || '—' }}</p>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">Вид заявки:</label>
                            <p>{{ form_data.request_kind || '—' }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Manager Information -->
                  <div class="detail-section">
                    <h5 class="section-title">
                      <i class="bi bi-person-workspace me-2 text-primary"></i>
                      Информация о менеджере
                    </h5>
                    <div class="card bg-light border-0 rounded-4 p-3">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">Менеджер:</label>
                            <p>{{ form_data.manager_name || '—' }}</p>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">Комментарий:</label>
                            <p class="comment-box">{{ form_data.manager_comment || '—' }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Fitter Information -->
                  <div class="detail-section">
                    <h5 class="section-title">
                      <i class="bi bi-tools me-2 text-primary"></i>
                      Информация о монтёре
                    </h5>
                    <div class="card bg-light border-0 rounded-4 p-3">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">Монтёр:</label>
                            <p>{{ form_data.fitter_name || '—' }}</p>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <label class="fw-semibold">Комментарий:</label>
                            <p class="comment-box">{{ form_data.fitter_comment || '—' }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Timeline Section -->
                <div class="col-12 col-lg-4">
                  <div class="timeline-wrapper sticky-lg-top pt-lg-0 pt-4" style="top: 20px;">
                    <div class="card border-0 shadow-sm rounded-4 p-4">
                      <h5 class="section-title mb-4">
                        <i class="bi bi-clock-history me-2 text-primary"></i>
                        Хронология
                      </h5>
                      <div class="position-relative">
                        <div class="timeline-line"></div>

                        <div class="timeline-item">
                          <div class="timeline-icon bg-dark text-white">
                            <i class="bi bi-file-earmark-plus"></i>
                          </div>
                          <div class="timeline-content">
                            <h5 class="mb-1">Заявка создана</h5>
                            <p class="timeline-date">{{ formattedTimestamp(form_data.date_created) }}</p>
                            <p class="timeline-description">{{ form_data.manager_name || 'Неизвестный менеджер' }}</p>
                          </div>
                        </div>

                        <div class="timeline-item" v-if="form_data.date_accepted">
                          <div class="timeline-icon bg-primary text-white">
                            <i class="bi bi-person-check"></i>
                          </div>
                          <div class="timeline-content">
                            <h5 class="mb-1">Принята монтёром</h5>
                            <p class="timeline-date">{{ formattedTimestamp(form_data.date_accepted) }}</p>
                            <p class="timeline-description">{{ form_data.fitter_name || 'Неизвестный монтёр' }}</p>
                          </div>
                        </div>

                        <div
                            class="timeline-item"
                            v-if="form_data.request_status_id === '12' ||
                              form_data.request_status_id === '13' ||
                              form_data.request_status_id === '5' ||
                              form_data.request_status_id === '14'"
                        >
                          <div class="timeline-icon bg-info text-white">
                            <i class="bi bi-tools"></i>
                          </div>
                          <div class="timeline-content">
                            <h5 class="mb-1">Выполнена монтёром</h5>
                            <p class="timeline-date">{{ formattedTimestamp(form_data.date_completed) }}</p>
                            <p class="timeline-description">{{ form_data.fitter_name || 'Неизвестный монтёр' }}</p>
                          </div>
                        </div>

                        <div class="timeline-item" v-if="form_data.date_closed">
                          <div class="timeline-icon bg-success text-white">
                            <i class="bi bi-check2-circle"></i>
                          </div>
                          <div class="timeline-content">
                            <h5 class="mb-1">Заявка закрыта</h5>
                            <p class="timeline-date">{{ formattedTimestamp(form_data.date_closed) }}</p>
                            <p class="timeline-description">{{ form_data.manager_name || 'Неизвестный менеджер' }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.content-wrapper {
  min-height: 100vh;
  background-color: #f8f9fa;
}

/* Section Styling */
.detail-section {
  margin-bottom: 2rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
}

.detail-item {
  margin-bottom: 0.5rem;
}

.detail-item label {
  display: block;
  margin-bottom: 0.25rem;
  color: #555;
}

.detail-item p {
  margin-bottom: 0;
  font-size: 1rem;
}

.comment-box {
  background-color: white;
  border-radius: 0.5rem;
  padding: 0.5rem;
  min-height: 40px;
  word-break: break-word;
}

/* Timeline Styling */
.timeline-wrapper {
  height: fit-content;
}

.timeline-line {
  position: absolute;
  left: 20px;
  top: 20px;
  bottom: 20px;
  width: 2px;
  background-color: #e9ecef;
}

.timeline-item {
  position: relative;
  padding: 0 0 1.5rem 3rem;
  display: flex;
  flex-direction: column;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-icon {
  position: absolute;
  left: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  z-index: 1;
  transition: transform 0.3s ease;
}

.timeline-content {
  position: relative;
  padding-left: 15px;
}

.timeline-date {
  font-size: 1.1rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.timeline-description {
  font-size: 1rem;
  color: #495057;
  margin-bottom: 0;
}

.timeline-item:hover .timeline-icon {
  transform: scale(1.1);
}

/* Remove cloak to prevent flash of unstyled content */
[v-cloak] {
  display: none;
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .timeline-wrapper {
    position: static !important;
  }
}

@media (max-width: 576px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .card-header div {
    align-self: flex-start;
  }

  .timeline-item {
    padding: 0 0 1.5rem 2.5rem;
  }

  .timeline-icon {
    width: 32px;
    height: 32px;
    font-size: 1rem;
  }
  .timeline-content {
    padding-left: 0;
  }
  .p-md-5 {
    padding: 1.5rem !important;
  }
}
</style>